import { USER_ROLES } from "@nlc/shared-types";

import { useRoleAccess } from "@/features/auth/permissions/useRoleAccess";
import { timezoneConverter } from "@/utils/timezoneConverter";

import type { ChatStory } from "../types";
import type { GroupedLogs } from "./message-parsers";

import {
  ChatLog,
  Feedback,
  MediaCollection,
  MediaFile,
  RandomFile,
  SystemLogsGroup,
} from "../components/message-types";
import { MyMessageCard } from "../components/MyMessageCard";
import { OthersMessageCard } from "../components/OthersMessageCard";
import { DISABLE_SHOW_MORE } from "../types";
import {
  isActivityLog,
  isGroupedActivityLogs,
  isGroupedAttachments,
  isGroupedFeedback,
  isImageAttachmentOrVideoAttachment,
  isRandomFileAttachment,
} from "./message-parsers";

export function ChatStoryContent({
  chatStory,
  handleOpenPreview,
  isCostumer,
  isCurrentUser,
  onEditClick,
  onFeedbackClick,
  timeZone,
}: {
  chatStory: ChatStory | GroupedLogs;
  handleOpenPreview: (id: number) => void;
  isCostumer: boolean;
  isCurrentUser: boolean;
  onEditClick?: (messageId: number, commentBatchId: string) => void;
  onFeedbackClick?: (groupId: string) => void;
  timeZone: null | string;
}) {
  const { canAccess } = useRoleAccess();

  if (isActivityLog(chatStory)) {
    return (
      <ChatLog
        diffModalContent={
          chatStory.oldValue
            ? {
                newValue: chatStory.newValue || "",
                oldValue: chatStory.oldValue || "",
              }
            : undefined
        }
        message={chatStory.comment || ""}
        shouldShowMore={DISABLE_SHOW_MORE.has(
          chatStory.asanaResourceSubtype ?? "",
        )}
        timestamp={timezoneConverter({
          timezone: isCostumer ? timeZone : null,
          type: "fromNow",
          utcDate: chatStory?.createdAt,
        })}
        tooltipDate={timezoneConverter({
          timezone: isCostumer ? timeZone : null,
          type: "dateFormat",
          utcDate: chatStory?.createdAt,
        })}
      />
    );
  }

  if (isGroupedActivityLogs(chatStory)) {
    return (
      <SystemLogsGroup
        data={chatStory}
        isCostumer={isCostumer}
        timeZone={timeZone}
      />
    );
  }

  let selectedMessage = (
    <div
      className="prose max-w-full overflow-hidden break-words"
      dangerouslySetInnerHTML={{
        __html: (chatStory as ChatStory)?.comment || "",
      }}
    />
  );

  if (isGroupedAttachments(chatStory)) {
    selectedMessage = (
      <MediaCollection data={chatStory} handleOpenPreview={handleOpenPreview} />
    );
  }

  if (isGroupedFeedback(chatStory)) {
    selectedMessage = (
      <Feedback data={chatStory} onFeedbackClick={onFeedbackClick} />
    );
  }

  if (isRandomFileAttachment(chatStory)) {
    selectedMessage = (
      <RandomFile
        data={chatStory}
        handleFeedback={onFeedbackClick}
        handleOpenPreview={handleOpenPreview}
      />
    );
  }

  if (isImageAttachmentOrVideoAttachment(chatStory)) {
    selectedMessage = (
      <MediaFile data={chatStory} handleOpenPreview={handleOpenPreview} />
    );
  }

  return (
    <div className="flex flex-col gap-3 w-full">
      {isCurrentUser ? (
        <MyMessageCard
          commentBatchId={(chatStory as ChatStory)?.commentBatchId || ""}
          isEdited={(chatStory as ChatStory)?.isEdited}
          messageId={(chatStory as ChatStory)?.id || 0}
          onEditClick={onEditClick}
          rawDate={timezoneConverter({
            timezone: (chatStory as ChatStory)?.user?.utcOffset ?? null,
            type: "dateFormat",
            utcDate: (chatStory as ChatStory)?.createdAt,
          })}
          timestamp={timezoneConverter({
            timezone: (chatStory as ChatStory)?.user?.utcOffset ?? null,
            type: "fromNow",
            utcDate: (chatStory as ChatStory)?.createdAt,
          })}
        >
          {selectedMessage}
        </MyMessageCard>
      ) : (
        <OthersMessageCard
          canDeleteComment={
            canAccess("chat.comment.delete") &&
            (chatStory as ChatStory)?.user?.roleName === USER_ROLES.DESIGNER
          }
          commentBatchId={(chatStory as ChatStory)?.commentBatchId || ""}
          isEdited={(chatStory as ChatStory)?.isEdited}
          rawDate={timezoneConverter({
            timezone: (chatStory as ChatStory)?.user?.utcOffset ?? null,
            type: "dateFormat",
            utcDate: (chatStory as ChatStory)?.createdAt,
          })}
          sender={(chatStory as ChatStory)?.user}
          timestamp={timezoneConverter({
            timezone: (chatStory as ChatStory)?.user?.utcOffset ?? null,
            type: "fromNow",
            utcDate: (chatStory as ChatStory)?.createdAt,
          })}
        >
          {selectedMessage}
        </OthersMessageCard>
      )}
    </div>
  );
}
